import * as React from 'react'
import clsx from 'clsx'
import { Paper, PaperProps } from '../Paper'
import { TransitionProps } from '../transitions'
import { SxProps, sx as sxFunc } from '../sx'
// import { Collapse } from '../Collapse'
import { useControlled } from '../../hooks/useControlled'
import { AccordionContext } from './AccordionContext'
import styles from './index.module.scss'

export interface AccordionClasses {
  /** Styles applied to the root element. */
  root: string
  /** Styles applied to the root element unless `square={true}`. */
  rounded: string
  /** State class applied to the root element if `expanded={true}`. */
  expanded: string
  /** State class applied to the root element if `disabled={true}`. */
  disabled: string
  /** Styles applied to the root element unless `disableGutters={true}`. */
  gutters: string
  /** Styles applied to the region element, the container of the children. */
  region: string
}

export interface AccordionProps extends Omit<PaperProps, 'onChange'> {
  /**
   * The content of the component.
   */
  children: NonNullable<React.ReactNode>
  /**
   * Override or extend the styles applied to the component.
   */
  classes?: Partial<AccordionClasses>
  /**
   * If `true`, expands the accordion by default.
   * @default false
   */
  defaultExpanded?: boolean
  /**
   * If `true`, the component is disabled.
   * @default false
   */
  disabled?: boolean
  /**
   * If `true`, it removes the margin between two expanded accordion items and the increase of height.
   * @default false
   */
  disableGutters?: boolean
  /**
   * If `true`, expands the accordion, otherwise collapse it.
   * Setting this prop enables control over the accordion.
   */
  expanded?: boolean
  /**
   * Callback fired when the expand/collapse state is changed.
   *
   * @param {React.SyntheticEvent} event The event source of the callback. **Warning**: This is a generic event not a change event.
   * @param {boolean} expanded The `expanded` state of the accordion.
   */
  onChange?: (event: React.SyntheticEvent, expanded: boolean) => void
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx?: SxProps
  /**
   * The component used for the transition.
   * [Follow this guide](/material-ui/transitions/#transitioncomponent-prop) to learn more about the requirements for this component.
   * @default Collapse
   */
  TransitionComponent?: React.JSXElementConstructor<
    TransitionProps & { children?: React.ReactElement<any, any> }
  >
  /**
   * Props applied to the transition element.
   * By default, the element is based on this [`Transition`](http://reactcommunity.org/react-transition-group/transition/) component.
   */
  TransitionProps?: TransitionProps
}

export const Accordion = React.forwardRef<HTMLDivElement, AccordionProps>(
  function Accordion(props, ref) {
    const {
      children: childrenProp,
      className,
      classes,
      defaultExpanded = false,
      disabled = false,
      disableGutters = false,
      expanded: expandedProp,
      onChange,
      square = false,
      // TransitionComponent = Collapse,
      // TransitionProps,
      sx,
      ...other
    } = props

    const [expanded, setExpandedState] = useControlled({
      controlled: expandedProp,
      default: defaultExpanded,
      name: 'Accordion',
      state: 'expanded',
    })

    const handleChange = React.useCallback(
      (event: React.SyntheticEvent) => {
        setExpandedState(!expanded)

        if (onChange) {
          onChange(event, !expanded)
        }
      },
      [expanded, onChange, setExpandedState]
    )

    const [summary, ...children] = React.Children.toArray(childrenProp)
    const contextValue = React.useMemo(
      () => ({ expanded, disabled, disableGutters, toggle: handleChange }),
      [expanded, disabled, disableGutters, handleChange]
    )

    const _classes = clsx(
      styles.root,
      classes?.root,
      !square && [styles.rounded, classes?.rounded],
      disabled && [styles.disabled, classes?.disabled],
      !disableGutters && [styles.enableGutters, classes?.gutters],
      expanded && [styles.expanded, classes?.expanded],
      className,
      sxFunc(sx)
    )

    return (
      <Paper className={_classes} ref={ref} square={square} {...other}>
        <AccordionContext.Provider value={contextValue}>
          {summary}
        </AccordionContext.Provider>
        {/* <TransitionComponent in={expanded} timeout="auto" {...TransitionProps}> */}
        {expanded ? (
          <div
            aria-labelledby={(summary as any).props?.id}
            id={(summary as any).props?.['aria-controls']}
            role="region"
            className={classes?.region}
          >
            {children}
          </div>
        ) : null}
        {/* </TransitionComponent> */}
      </Paper>
    )
  }
)
